/**
 * Authentication Hook and Provider
 * Includes: email verification check, resend verification, password reset
 */

import { createContext, useContext, useEffect, useRef, useState, ReactNode } from 'react';
import {
  User,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  reauthenticateWithCredential,
  EmailAuthProvider,
} from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from '../services/firebase';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  resendVerification: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  reauthenticate: (password: string) => Promise<boolean>;
  error: string | null;
  emailVerified: boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Guards against re-firing the post-verify welcome call within a single session.
  // Server-side Firestore flag is the source of truth across sessions/tabs.
  const postVerifyAttemptedRef = useRef<string | null>(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      setLoading(false);

      // Fire the post-verification welcome once per session, per uid, only when
      // the token reports verified. The callable is idempotent server-side.
      if (user && user.emailVerified && postVerifyAttemptedRef.current !== user.uid) {
        postVerifyAttemptedRef.current = user.uid;
        const fn = httpsCallable(functions, 'sendPostVerifyWelcome');
        fn({}).catch((err) => {
          console.warn('sendPostVerifyWelcome failed (non-fatal):', err);
        });
      }
    });

    return unsubscribe;
  }, []);

  const signIn = async (email: string, password: string) => {
    try {
      setError(null);
      await signInWithEmailAndPassword(auth, email, password);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Login failed';
      setError(message);
      throw err;
    }
  };

  const signOut = async () => {
    try {
      await firebaseSignOut(auth);
    } catch (err) {
      console.error('Sign out error:', err);
    }
  };

  const resendVerification = async () => {
    if (!user) throw new Error('Not logged in');
    // Route through our Cloud Function so the email is JAD-branded and sent
    // from info@jadenterprises.org via the hardened sender, instead of
    // Firebase Auth's default noreply@retell-calling.firebaseapp.com email.
    const fn = httpsCallable(functions, 'sendVerificationEmail');
    await fn({});
  };

  const resetPassword = async (email: string) => {
    await sendPasswordResetEmail(auth, email);
  };

  const reauthenticate = async (password: string): Promise<boolean> => {
    if (!user || !user.email) return false;
    try {
      const credential = EmailAuthProvider.credential(user.email, password);
      await reauthenticateWithCredential(user, credential);
      return true;
    } catch {
      return false;
    }
  };

  const emailVerified = user?.emailVerified ?? false;

  return (
    <AuthContext.Provider value={{ user, loading, signIn, signOut, resendVerification, resetPassword, reauthenticate, error, emailVerified }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
